import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { isAxiosError } from "axios";
import { useStationUsers } from "../hooks/useStationUsers";
import { useToast } from "../toast";
import { apiErrorMessage } from "../api/apiError";
import type { ReportGenUser } from "../interfaces/User";
import type {
  ExternalUserMapping,
  VendorDirectoryUser,
  VendorType,
} from "../interfaces/ExternalUserMapping";
import { BODYCAM_NOT_CONFIGURED } from "../interfaces/ExternalUserMapping";
import { vendorMeta, type Vendor } from "../../pages/ConnectBodycam/vendorConfig";
import { useUserMappings } from "@/features/bodycamMappings/hooks/useUserMappings";
import MapVendorOfficerDialog from "./MapVendorOfficerDialog";

/** A user with an account — invitation-only rows have no id to map against. */
type MappableUser = ReportGenUser & { id: string };

interface RowState {
  user: MappableUser;
  mappings: ExternalUserMapping[];
}

/** The directory reports the vendor in lowercase; vendorMeta is keyed upper. */
const VENDOR_BY_TYPE: Record<VendorType, Vendor> = {
  axon: "AXON",
  lenslock: "LENSLOCK",
  watchguard: "WATCHGUARD",
};

/**
 * `vendorType` is asserted, not validated, off the directory response, so a
 * vendor added backend-first arrives outside `VendorType` and misses both
 * tables. Naming it raw beats throwing on `.label` and taking the panel down.
 */
function labelForVendorType(vendorType: VendorType): string {
  const vendor: Vendor | undefined = VENDOR_BY_TYPE[vendorType];
  return vendor ? vendorMeta[vendor].label : vendorType;
}

export interface UserMappingsPanelProps {
  /** The station whose mappings are shown. Must match the caller's org. */
  stationId: string;
}

/**
 * Links KARDA users to the vendor-side officer ids bodycam evidence is
 * attributed to. Needs `users:admin`; both the Karda-admin station view and
 * a station admin's own Manage Users page render it.
 */
const UserMappingsPanel: React.FC<UserMappingsPanelProps> = ({ stationId }) => {
  const orgId = stationId;
  const {
    mappings,
    isLoadingMappings,
    mappingsLoadFailed,
    mappingsError,
    directory,
    isLoadingDirectory,
    directoryError,
    refreshDirectory,
    refreshMappings,
    createMapping,
    reassignMapping,
    deleteMapping,
    triggerResync,
  } = useUserMappings(orgId);

  const stationUsers = useStationUsers(orgId);
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dialogUser, setDialogUser] = useState<ReportGenUser | null>(null);
  const [unmappingId, setUnmappingId] = useState<string | null>(null);
  const [resyncing, setResyncing] = useState(false);

  // The vendor-users response names the bound vendor, so the panel needs no
  // separate integration lookup: no binding surfaces as an "unavailable"
  // directory carrying bodycam_integration_not_configured.
  const vendorLabel =
    directory?.status === "ready"
      ? labelForVendorType(directory.data.vendorType)
      : null;
  const bodycamNotConfigured =
    directory?.status === "unavailable" &&
    directory.code === BODYCAM_NOT_CONFIGURED;

  const { rows, orphanedMappings } = useMemo(() => {
    const grouped = new Map<string, ExternalUserMapping[]>();
    for (const m of mappings) {
      if (!m.active) continue;
      const list = grouped.get(m.userId) ?? [];
      list.push(m);
      grouped.set(m.userId, list);
    }
    const built: RowState[] = stationUsers.users
      .filter((u): u is MappableUser => u.id !== null)
      .map((u) => {
        const owned = grouped.get(u.id) ?? [];
        grouped.delete(u.id);
        return { user: u, mappings: owned };
      });
    // `grouped` still holding entries means the endpoint returned mappings for
    // user ids that aren't on this station's roster (deactivated or moved
    // users) — those rows have nobody to render against.
    return { rows: built, orphanedMappings: [...grouped.values()].flat() };
  }, [mappings, stationUsers.users]);

  // The mappings endpoint carries no vendor-side officer details, so names
  // for a mapped id come from joining against the vendor directory.
  const officerById = useMemo(() => {
    const map = new Map<string, VendorDirectoryUser>();
    if (directory?.status !== "ready") return map;
    for (const officer of directory.data.users) {
      map.set(officer.id, officer);
    }
    return map;
  }, [directory]);

  const officerLabel = useCallback(
    (externalUserId: string) => {
      const officer = officerById.get(externalUserId);
      return officer?.name || officer?.email || externalUserId;
    },
    [officerById],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const full = `${r.user.firstName} ${r.user.lastName}`.toLowerCase();
      return (
        full.includes(q) ||
        r.user.email?.toLowerCase().includes(q) ||
        r.user.badgeNumber?.toLowerCase().includes(q) ||
        r.mappings.some(
          (m) =>
            m.externalUserId.toLowerCase().includes(q) ||
            officerLabel(m.externalUserId).toLowerCase().includes(q),
        )
      );
    });
  }, [rows, search, officerLabel]);

  const stats = useMemo(() => {
    let mapped = 0;
    let multi = 0;
    for (const r of rows) {
      if (r.mappings.length >= 1) mapped += 1;
      if (r.mappings.length >= 2) multi += 1;
    }
    return {
      total: rows.length,
      mapped,
      unmapped: rows.length - mapped,
      multi,
    };
  }, [rows]);

  const userLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of stationUsers.users) {
      if (!u.id) continue;
      map.set(u.id, `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email);
    }
    return map;
  }, [stationUsers.users]);

  const resolveUserLabel = useCallback(
    (userId: string) => userLabelById.get(userId) ?? userId,
    [userLabelById],
  );

  const handleUnmap = async (
    externalUserId: string,
    userLabel: string,
  ): Promise<void> => {
    setUnmappingId(externalUserId);
    try {
      await deleteMapping(externalUserId);
      toast.success(`Unmapped ${userLabel}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to unmap"));
    } finally {
      setUnmappingId(null);
    }
  };

  const handleResync = async () => {
    setResyncing(true);
    try {
      const result = await triggerResync("all");
      toast.success(
        `Re-sync triggered: ${result.triggered} queued, ${result.alreadyMapped} already mapped, ${result.skipped} skipped`,
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to trigger re-sync"));
    } finally {
      setResyncing(false);
    }
  };

  const toggleRow = (userId: string) => {
    setExpanded((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleMapForUser = (user: ReportGenUser) => {
    setDialogUser(user);
  };

  const handleConfirmMap = async (
    user: ReportGenUser,
    externalUserId: string,
    conflict: ExternalUserMapping | null,
  ): Promise<void> => {
    if (!user.id) {
      toast.error(
        "This user has not accepted their invitation yet, so there is no account to map.",
      );
      return;
    }
    const userLabel = `${user.firstName} ${user.lastName}`.trim() || user.email;
    try {
      // A vendor id already linked elsewhere is re-pointed with PUT; POST
      // would 409 on the existing row.
      if (conflict) {
        await reassignMapping(externalUserId, user.id);
        toast.success(
          `Reassigned ${officerLabel(externalUserId)} → ${userLabel}`,
        );
      } else {
        await createMapping({ externalUserId, userId: user.id });
        toast.success(`Mapped ${userLabel} → ${officerLabel(externalUserId)}`);
      }
      setDialogUser(null);
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : null;
      if (status === 409) {
        toast.error(
          "This vendor ID is already mapped to another KARDA user. Refreshing the list.",
        );
        await refreshMappings();
      } else {
        toast.error(apiErrorMessage(err, "Failed to create mapping"));
      }
    }
  };

  const isLoading = isLoadingMappings || stationUsers.isLoading;

  // Neither list may render as empty when the request behind it failed: a
  // roster that lost its mappings shows every officer as "Unmapped", and
  // mapping one from there would duplicate a link that already exists. Both
  // loads are therefore prerequisites for any mapping action.
  const loadErrors = [
    mappingsLoadFailed ? mappingsError : null,
    stationUsers.loadFailed ? stationUsers.loadErrorMessage : null,
  ].filter((m): m is string => !!m);
  const loadFailed = loadErrors.length > 0;
  const canManageMappings = !isLoading && !loadFailed && !bodycamNotConfigured;

  const retryFailedLoads = () => {
    if (mappingsLoadFailed) void refreshMappings();
    if (stationUsers.loadFailed) void stationUsers.refresh();
  };

  if (loadFailed) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retryFailedLoads}>
              Try again
            </Button>
          }
        >
          {loadErrors.join(" ")} This is a loading problem — no user or mapping
          has gone anywhere.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {mappingsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mappingsError}
        </Alert>
      )}

      {bodycamNotConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This station has no bodycam integration connected yet. Connect a
          bodycam vendor to pull in officer accounts — you can still map
          officers by ID in the meantime.
        </Alert>
      )}

      {directoryError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => refreshDirectory()}
              disabled={isLoadingDirectory}
            >
              Retry
            </Button>
          }
        >
          {directoryError} You can still map officers by entering their vendor
          ID.
        </Alert>
      )}

      {orphanedMappings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {orphanedMappings.length} mapping
          {orphanedMappings.length === 1 ? "" : "s"} reference users who aren't
          on this station's roster:{" "}
          {orphanedMappings.map((m) => m.externalUserId).join(", ")}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper sx={{ p: 2, borderLeft: 4, borderColor: "success.main" }}>
            <Typography variant="body2" color="text.secondary">
              Mapped
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {stats.mapped}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper sx={{ p: 2, borderLeft: 4, borderColor: "warning.main" }}>
            <Typography variant="body2" color="text.secondary">
              Unmapped
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="warning.main">
              {stats.unmapped}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper sx={{ p: 2, borderLeft: 4, borderColor: "info.main" }}>
            <Typography variant="body2" color="text.secondary">
              Multi-device
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="info.main">
              {stats.multi}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <TextField
          size="small"
          placeholder="Search by name, email, badge, or vendor ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={
            resyncing ? <CircularProgress size={16} /> : <RefreshIcon />
          }
          onClick={handleResync}
          disabled={resyncing || !canManageMappings}
        >
          Re-sync All
        </Button>
        <Button variant="outlined" disabled>
          + Bulk Map
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={32} />
              <TableCell>User</TableCell>
              <TableCell>Badge</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Devices</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No users match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              filtered.map((row) => {
                const userLabel = `${row.user.firstName} ${row.user.lastName}`.trim() || row.user.email;
                const status =
                  row.mappings.length === 0
                    ? "unmapped"
                    : row.mappings.length === 1
                      ? "mapped"
                      : "multi";
                const isOpen = !!expanded[row.user.id];
                return (
                  <React.Fragment key={row.user.id}>
                    <TableRow hover>
                      <TableCell>
                        {row.mappings.length > 0 ? (
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(row.user.id)}
                          >
                            {isOpen ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>{userLabel}</TableCell>
                      <TableCell>{row.user.badgeNumber || "—"}</TableCell>
                      <TableCell>{row.user.email}</TableCell>
                      <TableCell>
                        {status === "unmapped" && (
                          <Chip
                            label="Unmapped"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {status === "mapped" && (
                          <Chip label="Mapped" size="small" color="success" />
                        )}
                        {status === "multi" && (
                          <Chip
                            label={`${row.mappings.length} devices`}
                            size="small"
                            color="info"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.mappings.length === 0
                          ? "—"
                          : row.mappings
                              .map((m) => officerLabel(m.externalUserId))
                              .join(", ")}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<AddLinkIcon fontSize="small" />}
                          onClick={() => handleMapForUser(row.user)}
                          disabled={!canManageMappings}
                        >
                          Map
                        </Button>
                      </TableCell>
                    </TableRow>
                    {row.mappings.length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ p: 0, borderBottom: 0 }}
                        >
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                              <Stack spacing={1}>
                                {row.mappings.map((m) => {
                                  return (
                                    <Paper
                                      key={m.externalUserId}
                                      sx={{
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                      }}
                                    >
                                      <Chip
                                        label={vendorLabel ?? m.integration}
                                        size="small"
                                      />
                                      <Box flex={1}>
                                        <Typography variant="body2">
                                          {officerLabel(m.externalUserId)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          ID: {m.externalUserId} · linked{" "}
                                          {new Date(
                                            m.createdAt,
                                          ).toLocaleDateString()}
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        color="error"
                                        startIcon={
                                          unmappingId === m.externalUserId ? (
                                            <CircularProgress size={14} />
                                          ) : (
                                            <LinkOffIcon fontSize="small" />
                                          )
                                        }
                                        disabled={
                                          unmappingId === m.externalUserId
                                        }
                                        onClick={() =>
                                          handleUnmap(
                                            m.externalUserId,
                                            userLabel,
                                          )
                                        }
                                      >
                                        Unmap
                                      </Button>
                                    </Paper>
                                  );
                                })}
                                <Button
                                  size="small"
                                  startIcon={<AddLinkIcon fontSize="small" />}
                                  onClick={() => handleMapForUser(row.user)}
                                  disabled={!canManageMappings}
                                  sx={{ alignSelf: "flex-start" }}
                                >
                                  Add another device mapping
                                </Button>
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <MapVendorOfficerDialog
        open={!!dialogUser}
        user={dialogUser}
        vendorLabel={vendorLabel}
        directory={directory}
        isLoadingDirectory={isLoadingDirectory}
        existingMappings={mappings}
        resolveUserLabel={resolveUserLabel}
        onClose={() => setDialogUser(null)}
        onConfirm={handleConfirmMap}
      />
    </Box>
  );
};

export default UserMappingsPanel;
